import React, { useState, useEffect, useRef } from "react";
import { statoNf } from "../utils/acquisti";
import { PulsanteFormazione } from "./RichiestaFormazione";
import { collection, query, where, onSnapshot, orderBy, limit, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { X, CheckSquare, Clock, CreditCard, Calendar, ShoppingCart, CheckCircle, LogOut } from "lucide-react";
import { VERSIONE_APP, DATA_VERSIONE } from "../utils/versione";
import { toLocalDateStr } from "../utils/dateUtils";
import { isTaskOverdue } from "../utils/taskHelpers";

function SezioneCard({ emoji, titolo, badge, badgeCol, sub, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick}
      onMouseOver={()=>setHover(true)} onMouseOut={()=>setHover(false)}
      style={{
        display:"flex",alignItems:"center",gap:12,width:"100%",padding:"10px 14px",
        border:"none",borderRadius:10,cursor:"pointer",textAlign:"left",fontFamily:"'Inter',sans-serif",
        background:hover?"#f5f3ff":"#fff",
        transition:"background .15s"
      }}>
      <div style={{width:36,height:36,borderRadius:10,background:"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
        {emoji}
      </div>
      <div style={{flex:1,overflow:"hidden"}}>
        <div style={{fontSize:12,fontWeight:700,color:"#111827"}}>{titolo}</div>
        {sub&&<div style={{fontSize:11,color:"#9ca3af",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sub}</div>}
      </div>
      {badge!=null&&badge>0&&(
        <span style={{
          background:badgeCol||"#7c3aed",color:"#fff",
          fontSize:10,fontWeight:800,padding:"2px 7px",borderRadius:20,flexShrink:0
        }}>{badge}</span>
      )}
      <span style={{fontSize:12,color:"#d1d5db"}}>→</span>
    </button>
  );
}

export default function MiaArea({ onNavigate, onClose }) {
  const { currentUser, logout } = useAuth();
  const { effectiveOrgId } = useOrg();
  const panelRef = useRef();

  // Stesso bug corretto oggi in Sidebar.js: "organization" da useAuth() è sempre il
  // profilo personale di chi è loggato, mai l'organizzazione in cui si è entrati con
  // "Entra" — qui serve invece quella davvero in uso, per il controllo Parco Mezzi.
  const [orgVisitata, setOrgVisitata] = useState(null);
  useEffect(() => {
    if (!effectiveOrgId) { setOrgVisitata(null); return; }
    return onSnapshot(doc(db, "organizations", effectiveOrgId), snap => {
      setOrgVisitata(snap.exists() ? snap.data() : null);
    });
  }, [effectiveOrgId]);


  const [tasks, setTasks] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [noteSpese, setNoteSpese] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [nf, setNf] = useState([]);
  const [approvazioni, setApprovazioni] = useState({ cartellini:0, spese:0 });
  const [dipendente, setDipendente] = useState(null);

  const uid = currentUser?.uid;
  const oggi = toLocalDateStr(new Date());

  useEffect(()=>{
    if(!effectiveOrgId||!uid) return;
    const unsubs = [];

    // Attività assegnate
    unsubs.push(onSnapshot(
      query(collection(db,"tasks"),where("organizationId","==",effectiveOrgId),where("assignedTo","==",uid)),
      snap=>setTasks(snap.docs.map(d=>({id:d.id,...d.data()})).filter(t=>t.status!=="completata"))
    ));

    // Presenze
    unsubs.push(onSnapshot(
      query(collection(db,"presenze"),where("orgId","==",effectiveOrgId),where("uid","==",uid)),
      snap=>setPresenze(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));

    // Note spese
    unsubs.push(onSnapshot(
      query(collection(db,"noteSpese"),where("orgId","==",effectiveOrgId),where("uid","==",uid)),
      snap=>setNoteSpese(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));

    // Scadenze personali
    unsubs.push(onSnapshot(
      query(collection(db,"scadenzario"),where("organizationId","==",effectiveOrgId)),
      snap=>setScadenze(snap.docs.map(d=>({id:d.id,...d.data()})))
    ));

    // NF create da me
    unsubs.push(onSnapshot(
      query(collection(db,"nuoveForniture"),where("organizationId","==",effectiveOrgId)),
      snap=>setNf(snap.docs.map(d=>({id:d.id,...d.data()})).filter(n=>n.createdBy===uid))
    ));

    // Approvazioni cartellini
    unsubs.push(onSnapshot(
      query(collection(db,"presenze"),where("orgId","==",effectiveOrgId),where("stato","==","inviata")),
      snap=>{
        // Controlla se questo utente è approvatore
        import("../pages/PresenzePage").then(()=>{}).catch(()=>{});
        setApprovazioni(prev=>({...prev, cartellini:snap.docs.length}));
      }
    ));

    // Approvazioni note spese
    unsubs.push(onSnapshot(
      query(collection(db,"noteSpese"),where("orgId","==",effectiveOrgId),where("stato","==","inviata")),
      snap=>setApprovazioni(prev=>({...prev, spese:snap.docs.length}))
    ));

    // Dipendente collegato
    unsubs.push(onSnapshot(
      query(collection(db,"dipendenti"),where("organizationId","==",effectiveOrgId),where("nexoraUid","==",uid)),
      snap=>setDipendente(snap.docs[0]?.data()||null)
    ));

    return ()=>unsubs.forEach(u=>u());
  },[effectiveOrgId,uid]);

  // Calcoli
  const taskInRitardo = tasks.filter(isTaskOverdue).length; // W452: regola unica (prima contava anche annullate e sospese)
  const meseCorrente = new Date().getMonth()+1;
  const annoCorrente = new Date().getFullYear();
  const cartellino = presenze.find(p=>p.mese===meseCorrente&&p.anno===annoCorrente);
  const speseMese = noteSpese.filter(n=>n.mese===meseCorrente&&n.anno===annoCorrente);
  const speseAperte = noteSpese.filter(n=>n.stato==="bozza"||n.stato==="inviata").length;
  const prossimi7 = new Date(); prossimi7.setDate(prossimi7.getDate()+7);
  const scadenzeVicine = scadenze.filter(s=>s.dataScadenza&&s.dataScadenza>=oggi&&s.dataScadenza<=toLocalDateStr(prossimi7)).length;
  const nfAperte = nf.filter(n=>!n.eliminata && ["Da gestire","In gestione","Ordinata"].includes(statoNf(n))).length;

  // Iniziali avatar
  const nomeCompleto = currentUser?.displayName||currentUser?.email||"?";
  const iniziali = nomeCompleto.split(" ").map(p=>p[0]).join("").substring(0,2).toUpperCase();

  // Stato cartellino
  const STATI_CART = {bozza:"In compilazione",inviata:"Inviato ⏳",approvata:"Approvato ✅",respinta:"Respinto ❌"};

  // Chiudi cliccando fuori
  useEffect(()=>{
    const handler = e=>{ if(panelRef.current&&!panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown",handler);
    return()=>document.removeEventListener("mousedown",handler);
  },[onClose]);

  const navAndClose = (page) => { onNavigate({page}); onClose(); };

  return (
    <div style={{position:"fixed",inset:0,zIndex:8000,display:"flex",justifyContent:"flex-end"}}>
      {/* Overlay semitrasparente */}
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,.3)"}} onClick={onClose}/>

      {/* Pannello */}
      <div ref={panelRef} style={{
        position:"relative",width:320,background:"#fff",
        boxShadow:"-8px 0 40px rgba(0,0,0,.15)",
        display:"flex",flexDirection:"column",animation:"slideInRight .2s ease",
        overflowY:"auto"
      }}>
        {/* Header profilo */}
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",padding:"20px 16px 16px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,color:"#fff",flexShrink:0}}>
                {iniziali}
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#fff"}}>{nomeCompleto}</div>
                {dipendente&&<div style={{fontSize:11,color:"rgba(255,255,255,.6)",marginTop:1}}>{dipendente.mansione||""}{dipendente.ruolo?` · ${dipendente.ruolo}`:""}</div>}
                {dipendente?.livelloProfessionalita&&<div style={{fontSize:10,marginTop:2}}>
                  <span style={{background:"rgba(255,255,255,.15)",color:"#c4b5fd",padding:"1px 6px",borderRadius:8,fontWeight:700}}>
                    Liv.{dipendente.livelloProfessionalita}
                  </span>
                </div>}
              </div>
            </div>
            <button onClick={onClose} style={{background:"rgba(255,255,255,.1)",border:"none",borderRadius:6,padding:6,cursor:"pointer",color:"rgba(255,255,255,.7)",display:"flex"}}>
              <X size={14}/>
            </button>
          </div>

          {/* Mini stats */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            {[
              {label:"Attività aperte", val:tasks.length, col:tasks.length>0?"#fde68a":"#6ee7b7"},
              {label:"In ritardo", val:taskInRitardo, col:taskInRitardo>0?"#fca5a5":"#6ee7b7"},
              {label:"Da approvare", val:approvazioni.cartellini+approvazioni.spese, col:"#c4b5fd"},
            ].map(({label,val,col})=>(
              <div key={label} style={{background:"rgba(255,255,255,.1)",borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
                <div style={{fontSize:18,fontWeight:800,color:col}}>{val}</div>
                <div style={{fontSize:9,color:"rgba(255,255,255,.5)",marginTop:2,lineHeight:1.2}}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sezioni */}
        <div style={{padding:"12px 8px",flex:1}}>
          <div style={{fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:".06em",padding:"0 6px 6px"}}>La mia area</div>

          <SezioneCard emoji="✔️" titolo="Le mie attività"
            badge={tasks.length} badgeCol={taskInRitardo>0?"#dc2626":"#7c3aed"}
            sub={taskInRitardo>0?`${taskInRitardo} in ritardo`:tasks.length===0?"Nessuna attività aperta":"Tutte in corso"}
            onClick={()=>navAndClose("my-tasks")}/>

          <SezioneCard emoji="🕐" titolo="Le mie ore"
            badge={null}
            sub={cartellino?`${STATI_CART[cartellino.stato]||cartellino.stato} — ${new Date().toLocaleDateString("it-IT",{month:"long"})}`:
              "Nessun cartellino questo mese"}
            onClick={()=>navAndClose("presenze")}/>

          <SezioneCard emoji="💳" titolo="Le mie spese"
            badge={speseAperte} badgeCol="#d97706"
            sub={speseAperte>0?`${speseAperte} nota/e in attesa`:"Nessuna spesa aperta"}
            onClick={()=>navAndClose("nota-spese")}/>

          <SezioneCard emoji="📅" titolo="Le mie scadenze"
            badge={scadenzeVicine} badgeCol="#dc2626"
            sub={scadenzeVicine>0?`${scadenzeVicine} in scadenza questa settimana`:"Nessuna scadenza imminente"}
            onClick={()=>navAndClose("scadenziario")}/>

          <SezioneCard emoji="🛒" titolo="Le mie richieste"
            badge={nfAperte} badgeCol="#0891b2"
            sub={nfAperte>0?`${nfAperte} richiesta/e aperte`:"Nessuna richiesta aperta"}
            onClick={()=>navAndClose("centro-acquisti")}/>

          {orgVisitata?.disponibilitaMezziAbilitato && (
            <SezioneCard emoji="📍" titolo="Disponibilità Mezzi e Attrezzature"
              badge={null}
              sub="Chi ha cosa, adesso"
              onClick={()=>navAndClose("disponibilita-mezzi")}/>
          )}

          {(approvazioni.cartellini>0||approvazioni.spese>0)&&(
            <>
              <div style={{fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:".06em",padding:"10px 6px 6px"}}>Da approvare</div>
              {approvazioni.cartellini>0&&(
                <SezioneCard emoji="✅" titolo="Cartellini da approvare"
                  badge={approvazioni.cartellini} badgeCol="#059669"
                  sub={`${approvazioni.cartellini} cartellino/i in attesa di approvazione`}
                  onClick={()=>navAndClose("presenze")}/>
              )}
              {approvazioni.spese>0&&(
                <SezioneCard emoji="💰" titolo="Note spese da approvare"
                  badge={approvazioni.spese} badgeCol="#059669"
                  sub={`${approvazioni.spese} nota/e spese in attesa`}
                  onClick={()=>navAndClose("nota-spese")}/>
              )}
            </>
          )}
        </div>

        {/* W426: formazione (solo titolare e amministratori: il pulsante si nasconde da solo) */}
        <div style={{padding:"4px 8px 0",flexShrink:0}}><PulsanteFormazione stile={{width:"100%",justifyContent:"center"}}/></div>
        {/* Footer logout */}
        <div style={{borderTop:"1px solid #f3f4f6",padding:"12px 8px",flexShrink:0}}>
          <button onClick={()=>{ logout(); onClose(); }}
            style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 14px",border:"1px solid #fee2e2",borderRadius:10,background:"#fff",cursor:"pointer",color:"#dc2626",fontSize:12,fontWeight:600,fontFamily:"'Inter',sans-serif"}}>
            <LogOut size={15} color="#dc2626"/>
            Esci da Nexiora
          </button>
          {/* versione in uso su questo dispositivo (utils/versione.js) */}
          <div style={{textAlign:"center",fontSize:10.5,color:"#9ca3af",marginTop:8}}>Nexiora {VERSIONE_APP} · {DATA_VERSIONE}</div>
        </div>
      </div>
      <style>{`@keyframes slideInRight{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}
